"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DescriptionDialog = exports.DescriptionPreview = void 0;
const tslib_1 = require("tslib");
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_forms_1 = require("@tableau/catalog-forms");
const catalog_help_url_1 = require("@tableau/catalog-help-url");
const catalog_messages_1 = require("@tableau/catalog-messages");
const NotificationInfoOutlineIcon_1 = require("@tableau/icons/NotificationInfoOutlineIcon");
const PencilBaseIcon_1 = require("@tableau/icons/PencilBaseIcon");
const SparklesIcon_1 = require("@tableau/icons/SparklesIcon");
const vizportalclient_common_components_1 = require("@tableau/vizportalclient-common-components");
const widgets_1 = require("@tableau/widgets");
const react_query_1 = require("@tanstack/react-query");
const formik_1 = require("formik");
const react_1 = tslib_1.__importDefault(require("react"));
const DescriptionRepository_1 = require("./DescriptionRepository");
const DescriptionTypes_1 = require("./DescriptionTypes");
const Disclaimer_1 = require("./Disclaimer");
function DescriptionPreview(_a) {
    var _b;
    var { contentId, contentType, noPreview } = _a, props = tslib_1.__rest(_a, ["contentId", "contentType", "noPreview"]);
    const displayReadMoreButton = react_1.default.useRef(props.startInReadMode);
    const [startInReadMode, setStartInReadMode] = react_1.default.useState(props.startInReadMode);
    const { anchorRef, getAnchorClientRect, showDialog, hideDialog, showingDialog } = (0, catalog_forms_1.useDialogAnchorGlue)();
    const einsteinEnabled = useEinsteinEnabled(contentType);
    const skipQuery = react_1.default.useRef((_b = props.skipQuery) !== null && _b !== void 0 ? _b : false);
    const { query } = (0, DescriptionRepository_1.useDescriptionRepository)({ contentId, contentType, skipQuery: skipQuery.current });
    const onClick = react_1.default.useCallback((readMode) => {
        if (skipQuery.current) {
            skipQuery.current = false;
        }
        setStartInReadMode(readMode);
        showDialog();
    }, [skipQuery, setStartInReadMode, showDialog]);
    const [displayDisclaimer, setDisplayDisclaimer] = react_1.default
        .useState(localStorage.getItem('show-catalog-gen-AI-disclaimer') === null);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("div", { style: { display: 'flex', maxHeight: 35, maxWidth: '100%' } },
            !noPreview && (react_1.default.createElement(widgets_1.TextFormatterDisplayWidget, { style: { margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '80%' }, markup: query.data || props.description || catalog_messages_1.Messages.NoDescriptionAvailable() })),
            react_1.default.createElement("div", { style: { marginTop: 5 } },
                react_1.default.createElement("span", { ref: anchorRef },
                    displayReadMoreButton && (react_1.default.createElement(widgets_1.ButtonWidget, { onClick: () => onClick(true), buttonType: widgets_1.ButtonType.LowEmphasis, density: 'low', disabled: showingDialog, style: { minWidth: 16, padding: 4 }, testId: 'open-dialog-in-read' }, catalog_messages_1.Messages.editDescriptionReadMore())),
                    react_1.default.createElement(widgets_1.ButtonWidget, { title: catalog_messages_1.Messages.ViewDescription_Edit(), onClick: () => onClick(false), buttonType: widgets_1.ButtonType.LowEmphasis, density: 'low', disabled: showingDialog, style: { minWidth: 16, padding: 4 }, testId: 'open-dialog-in-edit' }, einsteinEnabled
                        ? (react_1.default.createElement(react_1.default.Fragment, null,
                            react_1.default.createElement(SparklesIcon_1.SparklesIcon, { size: 14 }),
                            react_1.default.createElement(PencilBaseIcon_1.PencilBaseIcon, { size: 14 })))
                        : react_1.default.createElement(PencilBaseIcon_1.PencilBaseIcon, { size: 14 }))))),
        einsteinEnabled && showingDialog && (react_1.default.createElement(Disclaimer_1.Disclaimer, { setShowDisclaimer: (v) => {
                setDisplayDisclaimer(v);
            } })),
        showingDialog && !displayDisclaimer && (react_1.default.createElement(DescriptionDialog, Object.assign({}, props, { contentId: contentId, contentType: contentType, startInReadMode: startInReadMode, getAnchorClientRect: getAnchorClientRect, hideDialog: hideDialog, skipQuery: skipQuery.current })))));
}
exports.DescriptionPreview = DescriptionPreview;
const sharedDialogWidgetProps = {
    showCloseButton: true,
    isModal: true,
    type: widgets_1.DialogType.NormalWithTitleBar,
    positioningBehavior: widgets_1.DialogPositioningBehavior.Draggable,
    dialogWidth: 600,
    dialogContentStyle: { paddingBottom: 6 },
};
function DescriptionDialog(props) {
    const { contentId, contentType, hideDialog, description, skipQuery } = props;
    const [inEditMode, setInEditMode] = react_1.default.useState(() => props.startInReadMode ? false : true);
    const { query, mutation } = (0, DescriptionRepository_1.useDescriptionRepository)({
        skipQuery: skipQuery !== null && skipQuery !== void 0 ? skipQuery : false,
        description,
        contentId,
        contentType,
        hideDialog,
    });
    const queryClient = (0, react_query_1.useQueryClient)();
    const onReset = react_1.default.useCallback((_, __) => {
        try {
            void queryClient.cancelQueries({ queryKey: ['metadataSuggestions', contentId, contentType] });
        }
        finally {
            hideDialog();
        }
    }, [contentId, contentType, hideDialog, queryClient]);
    const dialogProps = Object.assign(Object.assign({}, sharedDialogWidgetProps), { onCloseRequest: props.hideDialog, handleGlassClick: props.hideDialog, getAnchorClientRect: props.getAnchorClientRect });
    if (query.isFetching) {
        /** TODO: for loading treatment, it might be nice to show a disabled textarea with a potential description */
        return (react_1.default.createElement(widgets_1.DialogWidget, Object.assign({}, dialogProps, { dialogHeight: 390, title: inEditMode ? catalog_messages_1.Messages.EditDescription() : catalog_messages_1.Messages.ViewDescription(), testId: 'description-dialog-loading' }),
            react_1.default.createElement("div", { style: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                } },
                react_1.default.createElement(widgets_1.ActivitySpinnerWidget, { spinnerSize: widgets_1.ActivitySpinnerSize.Large, testId: 'description-dialog-loading' }))));
    }
    if (!inEditMode) {
        return (react_1.default.createElement(widgets_1.DialogWidget, Object.assign({}, dialogProps, { title: catalog_messages_1.Messages.ViewDescription(), testId: 'description-dialog-view', footerContent: react_1.default.createElement(widgets_1.DialogFooterButtonsWidget, { buttons: [
                    {
                        children: catalog_messages_1.Messages.Cancel(),
                        onClick: props.hideDialog,
                        testId: 'preview-cancel',
                    },
                    {
                        children: catalog_messages_1.Messages.ViewDescription_Edit(),
                        onClick: () => setInEditMode(true),
                        buttonType: widgets_1.ButtonType.Primary,
                        testId: 'preview-edit',
                    },
                ] }) }),
            react_1.default.createElement(widgets_1.TextFormatterDisplayWidget, { markup: query.data || catalog_messages_1.Messages.NoDescriptionAvailable() })));
    }
    return (react_1.default.createElement(catalog_forms_1.FormikDialogWidget, Object.assign({}, dialogProps, { title: catalog_messages_1.Messages.EditDescription(), testId: 'description-dialog-edit', initialValues: { contentId, contentType, description: query.data || '' }, onSubmit: ({ description }) => tslib_1.__awaiter(this, void 0, void 0, function* () { return yield mutation.mutateAsync(Object.assign(Object.assign({}, props), { description })); }), onReset: onReset, validateOnChange: true, handleGlassClick: undefined, FooterContent: DescriptionDialogFooterContent }),
        react_1.default.createElement(DescriptionDialogHelpText, { contentType: contentType }),
        react_1.default.createElement(DescriptionDialogFields, Object.assign({}, props, { isLoading: query.isFetching || mutation.isLoading, testId: props.testId }))));
}
exports.DescriptionDialog = DescriptionDialog;
function useDraftForMeButton(params) {
    const einsteinEnabled = useEinsteinEnabled(params.contentType);
    const { button, isFetching } = (0, DescriptionRepository_1.useDraftForMe)(params);
    const element = einsteinEnabled
        ? (react_1.default.createElement(react_1.default.Fragment, null,
            button,
            react_1.default.createElement(DraftForMeHelp, null)))
        : null;
    return { element, isFetching };
}
function DraftForMeHelp() {
    const popoverInstance = react_1.default.useRef(null);
    const href = (0, catalog_help_url_1.useServerHelpUrlByToken)({ helpToken: 'core', editionOverride: 'online', prefix: 'gpt' });
    const link = (react_1.default.createElement(widgets_1.TextLinkWidget, { href: href, testId: 'help', target: '_blank', rel: 'noopener noreferrer' }, catalog_messages_1.Messages.LearnMoreLink()));
    const text = catalog_messages_1.Messages.editDescriptionDisclaimer({ learnMoreLink: '[LEARN_MORE_LINK]' });
    const id = 'what-is-this-description';
    const popoverContent = (react_1.default.createElement("div", { id: id }, (0, catalog_core_1.interpolateString)(text, { ['LEARN_MORE_LINK']: link }).map((t, i) => react_1.default.createElement(react_1.default.Fragment, { key: i }, t))));
    return (react_1.default.createElement(widgets_1.PopoverWidget, { title: catalog_messages_1.Messages.editDescriptionGenAiWhatIsThis(), "aria-describedby": id, placement: 'bottom', popoverContent: popoverContent, ref: popoverInstance, width: 200, triggerElement: react_1.default.createElement(widgets_1.ButtonWidget, { density: 'high', onClick: () => { var _a; return (_a = popoverInstance.current) === null || _a === void 0 ? void 0 : _a.toggle(true); }, buttonType: widgets_1.ButtonType.LowEmphasis, style: { marginRight: 'auto', minWidth: 16 } },
            react_1.default.createElement(NotificationInfoOutlineIcon_1.NotificationInfoOutlineIcon, { size: 16 })) }));
}
const validate = (current) => {
    const { message, valid } = (0, widgets_1.getMaxCharValidation)(current !== null && current !== void 0 ? current : '', DescriptionRepository_1.MAX_DESCRIPTION_LENGTH);
    if (valid === false && typeof message === 'string') {
        return message;
    }
    else {
        return undefined;
    }
};
const textAreaMessage = (current) => (0, widgets_1.getMaxCharValidation)(current !== null && current !== void 0 ? current : '', DescriptionRepository_1.MAX_DESCRIPTION_LENGTH).message;
function DescriptionDialogFields({ isLoading, testId }) {
    const { isSubmitting, status } = (0, formik_1.useFormikContext)();
    const feedback = (0, DescriptionRepository_1.useFeedback)();
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(catalog_forms_1.FormikTextFormatterWidget, { config: { name: 'description', validate }, label: catalog_messages_1.Messages.EditDescription_Label(), disabled: isSubmitting || isLoading || status === 'drafting', subtleValid: true, testId: testId, message: textAreaMessage }),
        feedback));
}
function DescriptionDialogFooterContent() {
    const { isSubmitting, isValid, dirty, handleSubmit, handleReset, initialValues } = (0, formik_1.useFormikContext)();
    const { Dialog, abandon } = (0, catalog_forms_1.useConfirmOnCloseRequest)(handleReset);
    const { element: draftForMeButton, isFetching } = useDraftForMeButton({
        contentId: initialValues.contentId,
        contentType: initialValues.contentType,
        validate: (m) => (0, widgets_1.getMaxCharValidation)(m, DescriptionRepository_1.MAX_DESCRIPTION_LENGTH).valid === false ? false : undefined,
    });
    const submitDisabled = !isValid || !dirty || isSubmitting || isFetching;
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(Dialog, null),
        react_1.default.createElement(FooterDiv, null,
            draftForMeButton,
            react_1.default.createElement(widgets_1.ButtonWidget, { type: 'reset', onClick: abandon, disabled: isSubmitting }, catalog_messages_1.Messages.Cancel()),
            react_1.default.createElement(widgets_1.ButtonWidget, { buttonType: widgets_1.ButtonType.Primary, type: 'submit', onClick: () => handleSubmit(), disabled: submitDisabled, showSpinner: isSubmitting ? true : undefined }, catalog_messages_1.Messages.Save()))));
}
const FooterDiv = (0, vizportalclient_common_components_1.divWithStyle)('dialog-confirm-cancel-footer', {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'flex-end',
    $nest: {
        '& > button ~ button': { marginLeft: widgets_1.TabStyles.Spacing.MediumSpace },
    },
});
function DescriptionDialogHelpText({ contentType }) {
    const einsteinEnabled = useEinsteinEnabled(contentType);
    const message = einsteinEnabled
        ? catalog_messages_1.Messages.EditDescription_PromptWithEinstein()
        : catalog_messages_1.Messages.EditDescription_Prompt();
    return react_1.default.createElement("p", { style: { marginTop: 6, paddingBottom: 8 } }, message);
}
function useEinsteinEnabled(contentType) {
    const { metadataSuggestionsEnabled } = (0, catalog_core_1.useUserSiteSettings)();
    const { suggestDescriptionsEnabled } = (0, catalog_core_1.useFeatureFlags)();
    return metadataSuggestionsEnabled && suggestDescriptionsEnabled && (0, DescriptionTypes_1.isGenerativeDescriptionContentType)(contentType);
}
//# sourceMappingURL=DescriptionComponents.js.map